import React, { useEffect, useState, memo } from 'react';
import {
  PlannedOutageEvent,
  PlannedOutageEvents,
  Scalars,
} from '../../../../__generated__/pge-types';
import { Marker } from 'react-map-gl';
import PlannedOutageIcon from '../icons/PlannedOutageIcon';
import { isValidCoordinate } from '../../utils/map';
import useGetOutageEventDetails from '../../hooks/useGetOutageEventDetails';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import { useErrorNotification } from '../common/error-notification';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import Supercluster, { ClusterFeature, PointFeature } from 'supercluster';
import { CONSTANTS } from '../../constants';
import { useTranslation } from '../../../../hooks/useTranslation';
import PlannedOutageClusterIcon from '../icons/PlannedOutageClusterIcon';
import { makeStyles } from '@material-ui/core';

export type PointProperties = {
  /** index back to `data` array to recover the original outage */
  outageIndex: number;
  totalCustomersImpacted: number;
};

export type ClusterBaseProps = {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: number;
};

export interface ClusterProperties {
  impactedCustomers: number;
}

const useStyles = makeStyles(() => ({
  cursorPointer: {
    cursor: 'pointer',
  },
  icon: {
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    transition: '0.2s',
    '&:hover': {
      width: '44px',
      height: '44px',
      zIndex: 5,
    },
  },
  iconSlected: {
    width: '44px',
    height: '44px',
    cursor: 'pointer',
  },
}));

const PlannedOutages = ({
  plannedOutages,
}: {
  plannedOutages: PlannedOutageEvents;
}) => {
  const {
    setOutageMarkerView,
    outageMarkerView,
    setSelectedOutageInfo,
  } = useOutageLeftPanelState();
  const { setPage } = useOutageResponsivePageTracker();
  const { wrapWithLoader } = useWrapWithLoader();
  const [
    selectedOutageEvent,
    setSelectedOutageEvent,
  ] = useState<PlannedOutageEvent | null>();
  const [isHovered, setIsHovered] = useState(false);
  const { mapInstance: map } = useOutageMapContext();
  const { t } = useTranslation();
  const validOutagesData =
    plannedOutages.plannedOutageEvents?.filter(o =>
      isValidCoordinate(parseFloat(o?.lat), parseFloat(o?.long)),
    ) || [];
  const classes = useStyles();

  // Convert outages to GeoJSON points
  const points = React.useMemo<PointFeature<PointProperties>[]>(() => {
    if (validOutagesData.length === 0) {
      return [];
    }
    return validOutagesData
      ?.map((item, i) => {
        const { lat, long, totalCustomersImpacted } = item!;

        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [long, lat] as [number, number],
          },
          properties: {
            outageIndex: i,
            totalCustomersImpacted: Number(totalCustomersImpacted) || 0,
          },
        };
      })
      .filter(Boolean) as PointFeature<PointProperties>[];
  }, [validOutagesData]);

  //Build supercluster with custom aggregation for impactedCustomers
  const index = React.useMemo(() => {
    const sc = new Supercluster<PointProperties, ClusterProperties>({
      radius: CONSTANTS.MAP_CONFIG.maxClusterRadius,
      maxZoom: CONSTANTS.MAP_CONFIG.maxClusterZoom,
      // aggregate sumCustomers per cluster
      map: props => ({ impactedCustomers: props.totalCustomersImpacted ?? 0 }),
      reduce: (accumulated, props) => {
        accumulated.impactedCustomers += props.impactedCustomers;
      },
    });
    // Supercluster expects GeoJSON features
    sc.load(points);
    return sc;
  }, [points]);

  const [bbox, setBbox] = React.useState<
    [number, number, number, number] | null
  >(null);
  const [zoom, setZoom] = React.useState<number>(0);

  //Keep bounds & zoom from the map to request clusters
  React.useEffect(() => {
    if (!map) {
      return;
    }

    const update = () => {
      const b = map.getBounds();
      if (!b) {
        return;
      }
      setBbox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      setZoom(map.getZoom());
    };

    update();
    //const evt = recalcOn === 'move' ? 'move' : 'moveend';
    const evt = 'move';
    map.on(evt, update);
    return () => {
      if (map) {
        map.off(evt, update);
      }
    };
  }, [map]);

  // 4) Compute visible clusters/features
  const clusters = React.useMemo(() => {
    if (!bbox) {
      return [] as Array<
        | PointFeature<PointProperties>
        | ClusterFeature<ClusterBaseProps & ClusterProperties>
      >;
    }
    const z = Math.round(zoom);
    return index.getClusters(bbox, z) as Array<
      | PointFeature<PointProperties>
      | ClusterFeature<ClusterBaseProps & ClusterProperties>
    >;
  }, [index, bbox, zoom]);

  const onSelectOutageEvent = wrapWithLoader(
    async (outage: PlannedOutageEvent) => {
      setSelectedOutageEvent(outage);

      setPage(ResponsivePageView.Details);
      setSelectedOutageInfo(outage);
      setOutageMarkerView(OutageMarkerView.Planned);
    },
  );

  useEffect(() => {
    if (selectedOutageEvent && map) {
      map?.flyTo({
        center: [selectedOutageEvent.long, selectedOutageEvent.lat],
        zoom: 15,
        duration: 0, // ms
        essential: true, // respects prefers-reduced-motion
      });
    }
  }, [selectedOutageEvent, map]);

  useEffect(() => {
    if (!outageMarkerView) {
      setSelectedOutageEvent(null);
    }
  }, [outageMarkerView]);

  return (
    <>
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates as [number, number];
        const props = feature.properties as ClusterBaseProps &
          ClusterProperties;
        const { cluster_id, impactedCustomers, point_count } = props;

        const title =
          point_count > 1 ? t('PLANNED_OUTAGES') : t('PLANNED_OUTAGE');

        // Cluster feature (has aggregated props)
        if ((feature.properties as ClusterBaseProps).cluster) {
          const onClickCluster = () => {
            const expZoom = Math.min(
              index.getClusterExpansionZoom(cluster_id),
              CONSTANTS.MAP_CONFIG.maxClusterZoom + 2,
            );

            map?.easeTo({
              center: [lng, lat],
              zoom: expZoom,
              duration: 500,
            });
          };

          const markerClusterProps = {
            id: cluster_id,
            title,
            text: String(point_count),
            lat: lat,
            long: lng,
            className: classes.icon,
            onClick: onClickCluster,
          };

          return (
            <OutageClusterMarker
              key={`p-cluster-m-${cluster_id}`}
              {...markerClusterProps}
            />
          );
        }

        // Unclustered point -> render outage marker
        const outageIndex = (feature.properties as PointProperties).outageIndex;
        const outage = validOutagesData[outageIndex];

        const isSelectedOutage =
          outage && selectedOutageEvent?.eventId === outage.eventId;

        const markerProps = {
          id: outageIndex,
          title,
          outage: outage!,
          isSelectedOutage: !!isSelectedOutage,
          className: isSelectedOutage ? classes.iconSlected : classes.icon,
          onClick: onSelectOutageEvent,
        };

        return (
          <OutageMarker key={`p-outage-m-${outageIndex}`} {...markerProps} />
        );
      })}
    </>
  );
};

interface OutageClusterMarkerProps {
  id: number;
  title: string;
  text: string;
  className: string;
  lat: Scalars['Latitude'];
  long: Scalars['Longitude'];
  onClick: () => void;
}

const OutageClusterMarker = React.memo(
  ({
    id,
    title,
    text,
    className,
    lat,
    long,
    onClick,
  }: OutageClusterMarkerProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Marker
        key={`cluster-${id}`}
        longitude={long}
        latitude={lat}
        anchor="center"
        style={{ zIndex: isHovered ? 5 : 2 }}
        onClick={() => onClick()}
      >
        <div
          onClick={e => {
            e.preventDefault(); //Prevents triggering the PSPS polygon onclick handler
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <PlannedOutageClusterIcon
            title={title}
            text={text}
            size={40}
            className={className}
          />
        </div>
      </Marker>
    );
  },
);

interface OutageMarkerProps {
  id: number;
  title: string;
  isSelectedOutage: boolean;
  className: string;
  outage: PlannedOutageEvent;
  onClick: (outage: PlannedOutageEvent) => void;
}

const OutageMarker = React.memo(
  ({
    id,
    title,
    isSelectedOutage,
    className,
    outage,
    onClick,
  }: OutageMarkerProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Marker
        key={`outage-${id}`}
        longitude={outage.long}
        latitude={outage.lat}
        anchor="center"
        style={{ zIndex: isSelectedOutage || isHovered ? 5 : 2 }}
        onClick={e => {
          if (outage) {
            void onClick(outage);
          }
        }}
      >
        <div
          onClick={e => {
            e.preventDefault(); //Prevents triggering the PSPS polygon onclick handler
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <PlannedOutageIcon className={className} title={title} />
        </div>
      </Marker>
    );
  },
);

export const PlannedOutagesOverlay = memo(PlannedOutages);
